import React, { useCallback, useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { maintenanceFlow } from '../../help/flows';
import { WORK_ORDER_STAGES } from '../../help/statusStages';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { ItemService } from '../../services/item.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import RepairBillsPanel from './RepairBillsPanel';
import {
    CHECKLIST_RESULTS, ChecklistResult, FailureCodeDTO, HOLD_REASONS, HoldReason, TechnicianDTO,
    WorkOrderDetailDTO, mntLabel,
} from '../../interfaces/maintenance';
import { Item } from '../../interfaces/item.interface';
import { WO_PRIORITY_COLOR, WO_STATUS_COLOR, fmtDateTime, fmtMinutes, localToIso, money } from './woCommon';

type DialogKind = 'assign' | 'hold' | 'complete' | 'close' | 'cancel' | 'reopen' | 'labour' | 'checklist' | 'parts' | 'attach' | 'external' | null;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{children || '-'}</Typography>
    </Box>
);

/** One work order: lifecycle, labour, checklist, parts, external repair. Values are hidden unless the server sends them. */
const WorkOrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_WO_MANAGE);
    const canSubmitRepairBill = hasPermission(PERMISSIONS.MAINTENANCE_REPAIR_BILL_SUBMIT);
    const canWork = canManage || hasPermission(PERMISSIONS.MAINTENANCE_WO_WORK);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [detail, setDetail] = useState<WorkOrderDetailDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState<DialogKind>(null);
    const [saving, setSaving] = useState(false);
    const [technicians, setTechnicians] = useState<TechnicianDTO[]>([]);
    const [codes, setCodes] = useState<FailureCodeDTO[]>([]);
    const [items, setItems] = useState<Item[]>([]);

    // dialog form state
    const [technicianId, setTechnicianId] = useState('');
    const [holdReason, setHoldReason] = useState<HoldReason>('WAITING_PARTS');
    const [note, setNote] = useState('');
    const [workDone, setWorkDone] = useState('');
    const [stillDown, setStillDown] = useState(false);
    const [backInService, setBackInService] = useState('');
    const [symptomId, setSymptomId] = useState('');
    const [causeId, setCauseId] = useState('');
    const [actionId, setActionId] = useState('');
    const [meterId, setMeterId] = useState('');
    const [meterValue, setMeterValue] = useState('');
    const [meterReadAt, setMeterReadAt] = useState('');
    const [reason, setReason] = useState('');
    const [machineDownAgain, setMachineDownAgain] = useState(false);
    const [minutes, setMinutes] = useState('');
    const [task, setTask] = useState('');
    const [itemId, setItemId] = useState('');
    const [qty, setQty] = useState('1');
    const [refNo, setRefNo] = useState('');
    const [vendor, setVendor] = useState('');
    const [costMemo, setCostMemo] = useState('');
    const [billRef, setBillRef] = useState('');

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            setDetail(await MaintenanceService.getWorkOrder(id));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the work order');
        } finally {
            setLoading(false);
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const open = (kind: Exclude<DialogKind, null>) => {
        setTechnicianId(detail?.order.assignedTo ?? ''); setHoldReason('WAITING_PARTS'); setNote(''); setWorkDone(detail?.order.workDone ?? '');
        setStillDown(false); setBackInService(''); setSymptomId(detail?.order.symptomCodeId ?? '');
        setCauseId(detail?.order.causeCodeId ?? ''); setActionId(detail?.order.actionCodeId ?? '');
        setMeterId(''); setMeterValue(''); setMeterReadAt(''); setReason('');
        setMachineDownAgain(false); setMinutes(''); setTask(''); setItemId(''); setQty('1'); setRefNo('');
        setVendor(detail?.order.externalVendorName ?? ''); setCostMemo(detail?.order.externalCostMemo != null ? String(detail.order.externalCostMemo) : '');
        setBillRef(detail?.order.externalBillRef ?? '');
        setDialog(kind);
        if (kind === 'assign' || (kind === 'labour' && canManage)) {
            MaintenanceService.listTechnicians(institutionId).then(setTechnicians).catch(() => setTechnicians([]));
        }
        if (kind === 'complete' || kind === 'close') {
            MaintenanceService.listFailureCodes(institutionId).then(setCodes).catch(() => setCodes([]));
        }
        if (kind === 'parts') searchItems('');
    };

    const searchItems = (text: string) => {
        ItemService.getItemsByInstitution(0, 30, text).then(r => setItems(r.content)).catch(() => undefined);
    };

    const run = async (fn: () => Promise<unknown>, ok: string) => {
        try {
            setSaving(true);
            await fn();
            toast.showSuccess(ok);
            setDialog(null);
            await load();
        } catch (e) {
            if ((e as { response?: { status?: number } })?.response?.status === 409) {
                toast.showError('This work order was changed by someone else. It has been reloaded - please check and try again.');
                setDialog(null);
                await load();
            } else {
                toast.showApiError(e, 'The action failed');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading && !detail) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    if (!detail || !id) return <Box sx={{ p: 2 }}><Typography>Work order not found</Typography></Box>;

    const wo = detail.order;
    const values = detail.canSeeValues;
    const live = wo.status !== 'CLOSED' && wo.status !== 'CANCELLED';
    // WorkOrderService.requireLabourState: labour is refused while the order is Open, Closed or Cancelled.
    const labourAllowed = live && wo.status !== 'OPEN';
    const kindCodes = (k: string) => codes.filter(c => c.kind === k && c.active);

    const ver = wo.version;

    const submit = () => {
        switch (dialog) {
            case 'assign':
                return run(() => MaintenanceService.workOrderAction(id, 'assign', { technicianId }, ver), 'Technician assigned');
            case 'hold':
                return run(() => MaintenanceService.workOrderAction(id, 'hold', { reason: holdReason, note: note.trim() || undefined }, ver), 'Work order on hold');
            case 'complete':
                return run(() => MaintenanceService.completeWorkOrder(id, {
                    workDone: workDone.trim(), stillDown, backInServiceAt: stillDown ? undefined : localToIso(backInService),
                    symptomCodeId: symptomId || undefined, causeCodeId: causeId || undefined, actionCodeId: actionId || undefined,
                    meterReading: meterId && meterValue.trim() !== ''
                        ? { meterId, value: Number(meterValue), readAt: localToIso(meterReadAt) } : undefined,
                }, ver), 'Work order completed');
            case 'close':
                return run(() => MaintenanceService.closeWorkOrder(id, {
                    causeCodeId: causeId || undefined, actionCodeId: actionId || undefined,
                }, ver), 'Work order closed');
            case 'cancel':
                return run(() => MaintenanceService.workOrderAction(id, 'cancel', { reason: reason.trim() }, ver), 'Work order cancelled');
            case 'reopen':
                return run(() => MaintenanceService.workOrderAction(id, 'reopen', { machineDownAgain }, ver), 'Work order reopened');
            case 'labour':
                return run(() => MaintenanceService.addLabour(id, {
                    technicianId: technicianId || undefined, minutes: minutes.trim() === '' ? undefined : Number(minutes), note: note.trim() || undefined,
                }), 'Labour added');
            case 'checklist':
                return run(() => MaintenanceService.addChecklistItem(id, task.trim()), 'Checklist item added');
            case 'parts':
                return run(() => MaintenanceService.requestParts(id, [{ itemId, qty: Number(qty) }]), 'Parts requisition created');
            case 'attach':
                return run(() => MaintenanceService.attachRequisition(id, refNo.trim()), 'Requisition attached');
            case 'external':
                return run(() => MaintenanceService.saveExternalRepair(id, {
                    vendorName: vendor.trim() || undefined, costMemo: costMemo.trim() === '' ? undefined : Number(costMemo), billRef: billRef.trim() || undefined,
                }, ver), 'External repair saved');
            default:
                return Promise.resolve();
        }
    };

    const canSubmit = (() => {
        switch (dialog) {
            case 'assign': return technicianId !== '';
            case 'complete': return workDone.trim() !== '';
            case 'cancel': return reason.trim() !== '';
            case 'checklist': return task.trim() !== '';
            case 'parts': return itemId !== '' && Number(qty) > 0;
            case 'attach': return refNo.trim() !== '';
            case 'labour': return minutes.trim() !== '' && Number(minutes) > 0;
            default: return true;
        }
    })();

    const codeSelect = (label: string, kind: string, value: string, set: (v: string) => void, helpAttr: Record<string, string>) => (
        <SearchableSelect
            options={kindCodes(kind)} value={value} size="small" label={label} {...helpAttr}
            getOptionLabel={c => `${c.code} - ${c.name}`} onChange={v => set(v)}
        />
    );

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.workOrder.page">
            <FlowStrip data-help="maint.workOrder.flow" stages={maintenanceFlow({ requestNo: wo.breakdownRequestId ? 'Linked request' : null, workOrderNo: wo.woNo, parts: detail.parts.map(p => ({ requisitionId: p.requisitionId, ref: p.refNo, raisedByMe: p.raisedByMe })), closed: wo.status === 'CLOSED' })} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <Button data-help="maint.workOrder.action.back" size="small" onClick={() => navigate('/maintenance/work-orders')}>Back</Button>
                <Typography variant="h5" sx={{ mr: 1 }}>{wo.woNo}</Typography>
                <StatusStagesChip size="medium" data-help="maint.workOrder.statusStages" label={mntLabel(wo.status)} status={wo.status} stages={WORK_ORDER_STAGES} color={WO_STATUS_COLOR[wo.status]} />
                <Chip label={mntLabel(wo.priority)} color={WO_PRIORITY_COLOR[wo.priority]} variant="outlined" />
                <Chip label={mntLabel(wo.type)} variant="outlined" />
                {wo.machineDown && <Chip label="Machine down" color="error" />}
                {wo.archivedAt && (
                    <Chip data-help="maint.workOrder.archivedChip" variant="outlined" label={`Archived on ${fmtDateTime(wo.archivedAt)}`} />
                )}
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>{wo.title}</Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {canManage && (wo.status === 'OPEN' || wo.status === 'ASSIGNED') && <Button data-help="maint.workOrder.assign" variant="outlined" onClick={() => open('assign')}>{wo.assignedTo ? 'Reassign' : 'Assign'}</Button>}
                {canWork && wo.status === 'ASSIGNED' && (
                    <Button data-help="maint.workOrder.start" variant="contained" onClick={() => run(() => MaintenanceService.workOrderAction(id, 'start', {}, ver), 'Work started')}>Start work</Button>
                )}
                {canWork && wo.status === 'IN_PROGRESS' && <Button data-help="maint.workOrder.hold" variant="outlined" onClick={() => open('hold')}>Put on hold</Button>}
                {canWork && wo.status === 'ON_HOLD' && (
                    <Button data-help="maint.workOrder.resume" variant="contained" onClick={() => run(() => MaintenanceService.workOrderAction(id, 'resume', {}, ver), 'Work resumed')}>Resume</Button>
                )}
                {canWork && wo.status === 'IN_PROGRESS' && <Button data-help="maint.workOrder.complete" variant="contained" onClick={() => open('complete')}>Complete</Button>}
                {canManage && wo.status === 'COMPLETED' && <Button data-help="maint.workOrder.close" variant="contained" onClick={() => open('close')}>Close</Button>}
                {canManage && wo.status === 'COMPLETED' && <Button data-help="maint.workOrder.reopen" variant="outlined" onClick={() => open('reopen')}>Reopen</Button>}
                {canManage && wo.archivedAt && (
                    <Button data-help="maint.workOrder.restoreArchive" variant="outlined"
                        onClick={() => run(() => MaintenanceService.restoreWorkOrderFromArchive(id), 'Restored from the archive')}>Restore from archive</Button>
                )}
                {canManage && live && wo.status !== 'COMPLETED' && <Button data-help="maint.workOrder.cancel" color="error" onClick={() => open('cancel')}>Cancel</Button>}
            </Box>

            {wo.status === 'ON_HOLD' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    On hold: {mntLabel(wo.holdReason)}{wo.holdNote ? ` - ${wo.holdNote}` : ''}
                </Alert>
            )}
            {wo.status === 'CANCELLED' && <Alert severity="info" sx={{ mb: 2 }}>Cancelled: {wo.cancelReason}</Alert>}

            <Paper sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="maint.workOrder.facts">
                <Field label="Machine">{wo.equipmentCode ? `${wo.equipmentCode} ${wo.equipmentName ?? ''}` : wo.locationName}</Field>
                <Field label="Technician">{wo.assignedToName}</Field>
                <Field label="Created">{fmtDateTime(wo.createdAt)}</Field>
                <Field label="Started">{fmtDateTime(wo.startedAt)}</Field>
                <Field label="Completed">{fmtDateTime(wo.completedAt)}</Field>
                <Field label="Down from">{fmtDateTime(wo.downFrom)}</Field>
                <Field label="Back in service">{fmtDateTime(wo.backInServiceAt)}</Field>
                <Field label="Downtime">{fmtMinutes(wo.downtimeMinutes)}</Field>
                <Box sx={{ flex: '1 1 100%' }}><Field label="Description">{wo.description}</Field></Box>
                <Box sx={{ flex: '1 1 100%' }}><Field label="Work done">{wo.workDone}</Field></Box>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.workOrder.labour">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1">Labour</Typography>
                    {canWork && labourAllowed && <Button data-help="maint.workOrder.addLabour" size="small" onClick={() => open('labour')}>Add labour</Button>}
                </Box>
                <Table size="small">
                    <TableHead><TableRow><TableCell data-help="maint.workOrder.lcol.tech">Technician</TableCell><TableCell data-help="maint.workOrder.lcol.time">Time</TableCell><TableCell data-help="maint.workOrder.lcol.note">Note</TableCell><TableCell data-help="maint.workOrder.lcol.actions" /></TableRow></TableHead>
                    <TableBody>
                        {detail.labour.length === 0 && <TableRow><TableCell colSpan={4} align="center">No labour recorded</TableCell></TableRow>}
                        {detail.labour.map(l => (
                            <TableRow key={l.id}>
                                <TableCell>{l.technicianName ?? '-'}</TableCell>
                                <TableCell>{fmtMinutes(l.minutes)}</TableCell>
                                <TableCell>{l.note ?? '-'}</TableCell>
                                <TableCell align="right">
                                    {canWork && labourAllowed && (
                                        <IconButton
                                            data-help="maint.workOrder.action.removeLabour" size="small" aria-label="Remove labour"
                                            onClick={() => run(() => MaintenanceService.deleteLabour(id, l.id), 'Labour removed')}
                                        ><DeleteIcon fontSize="small" /></IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.workOrder.checklist">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1">Checklist</Typography>
                    {canWork && live && <Button data-help="maint.workOrder.addCheck" size="small" onClick={() => open('checklist')}>Add item</Button>}
                </Box>
                {detail.checklist.length === 0 && <Typography variant="body2">No checklist items</Typography>}
                {detail.checklist.map(c => (
                    <Box key={c.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', py: 0.5 }}>
                        <Typography variant="body2" sx={{ flex: '1 1 240px' }}>{c.task}</Typography>
                        <TextField
                            select size="small" label="Result" value={c.result ?? ''} data-help="maint.workOrder.checkResult" sx={{ flex: '1 1 120px', maxWidth: 180 }}
                            disabled={!canWork || !live}
                            onChange={e => run(() => MaintenanceService.setChecklistResult(id, c.id, e.target.value as ChecklistResult, c.remark ?? undefined), 'Checklist updated')}
                        >
                            {CHECKLIST_RESULTS.map(r => <MenuItem key={r} value={r}>{r === 'NA' ? 'N/A' : mntLabel(r)}</MenuItem>)}
                        </TextField>
                    </Box>
                ))}
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.workOrder.parts">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1">Spare parts</Typography>
                    {canWork && live && (
                        <Box>
                            <Button data-help="maint.workOrder.requestParts" size="small" onClick={() => open('parts')}>Request parts</Button>
                            {canManage && <Button data-help="maint.workOrder.attachReq" size="small" onClick={() => open('attach')}>Attach requisition</Button>}
                        </Box>
                    )}
                </Box>
                {detail.parts.length === 0 && <Typography variant="body2">No parts requested</Typography>}
                {detail.parts.map(p => (
                    <Box key={p.id} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                            {p.refNo ?? p.requisitionId} <Chip size="small" label={mntLabel(p.status)} />
                            {values && p.issuedValue != null && <> &nbsp;Issued value: {money(p.issuedValue)}</>}
                        </Typography>
                        {p.lines.map(l => (
                            <Typography key={l.itemId} variant="caption" display="block" sx={{ pl: 2 }}>
                                {l.itemName ?? l.itemCode}: requested {l.requiredQty}, approved {l.approvedQty ?? '-'}, issued {l.issued ?? 0}
                            </Typography>
                        ))}
                    </Box>
                ))}
                {values && wo.partsValue != null && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Total parts value: {money(wo.partsValue)}</Typography>
                )}
            </Paper>

            {(canManage || wo.externalVendorName) && (
                <Paper sx={{ p: 2, mb: 2 }} data-help-section="maint.workOrder.external">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1">External repair</Typography>
                        {canManage && live && <Button data-help="maint.workOrder.editExternal" size="small" onClick={() => open('external')}>Edit</Button>}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Field label="Vendor">{wo.externalVendorName}</Field>
                        {values && <Field label="Cost (memo)">{wo.externalCostMemo != null ? money(wo.externalCostMemo) : ''}</Field>}
                        {values && <Field label="Bill reference">{wo.externalBillRef}</Field>}
                    </Box>
                </Paper>
            )}

            <RepairBillsPanel workOrderId={wo.id} canSubmit={canSubmitRepairBill} live={live} />

            <Dialog open={dialog !== null} onClose={() => setDialog(null)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'maint.woDialog.page' } as any}>
                <DialogTitle>{dialogTitle(dialog)}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {(dialog === 'assign' || (dialog === 'labour' && canManage)) && (
                            <SearchableSelect
                                options={technicians} value={technicianId} size="small" label="Technician" data-help="maint.woDialog.field.technician"
                                getOptionId={t => t.userId} getOptionLabel={t => t.name} onChange={v => setTechnicianId(v)}
                            />
                        )}
                        {dialog === 'hold' && (
                            <TextField select size="small" label="Reason" value={holdReason} data-help="maint.woDialog.field.holdReason" onChange={e => setHoldReason(e.target.value as HoldReason)}>
                                {HOLD_REASONS.map(r => <MenuItem key={r} value={r}>{mntLabel(r)}</MenuItem>)}
                            </TextField>
                        )}
                        {(dialog === 'hold' || dialog === 'labour') && (
                            <TextField size="small" label="Note" value={note} multiline minRows={2} data-help="maint.woDialog.field.note" onChange={e => setNote(e.target.value)} />
                        )}
                        {dialog === 'labour' && (
                            <TextField size="small" type="number" label="Minutes worked" value={minutes} data-help="maint.woDialog.field.minutes" onChange={e => setMinutes(e.target.value)} />
                        )}
                        {dialog === 'complete' && (
                            <>
                                <TextField size="small" label="Work done" value={workDone} multiline minRows={3} required data-help="maint.woDialog.field.workDone" onChange={e => setWorkDone(e.target.value)} />
                                <FormControlLabel
                                    control={<Checkbox checked={stillDown} onChange={e => setStillDown(e.target.checked)} />}
                                    label="Machine is still down" data-help="maint.woDialog.field.stillDown"
                                />
                                {!stillDown && wo.machineDown && (
                                    <TextField
                                        size="small" type="datetime-local" label="Back in service" value={backInService} data-help="maint.woDialog.field.backInService"
                                        InputLabelProps={{ shrink: true }} onChange={e => setBackInService(e.target.value)}
                                    />
                                )}
                                {codeSelect('Symptom', 'SYMPTOM', symptomId, setSymptomId, { 'data-help': 'maint.woDialog.field.symptom' })}
                                {codeSelect('Cause', 'CAUSE', causeId, setCauseId, { 'data-help': 'maint.woDialog.field.cause' })}
                                {codeSelect('Action taken', 'ACTION', actionId, setActionId, { 'data-help': 'maint.woDialog.field.action' })}
                                {(detail.meters ?? []).length > 0 && (
                                    <>
                                        <SearchableSelect
                                            options={detail.meters ?? []} value={meterId} size="small" label="Meter reading (optional)" data-help="maint.woDialog.field.meter"
                                            getOptionLabel={m => `${m.code} - ${m.name} (${m.unit})`} onChange={v => setMeterId(v)}
                                        />
                                        {meterId !== '' && (
                                            <>
                                                <TextField
                                                    size="small" type="number" label="Reading" value={meterValue} data-help="maint.woDialog.field.reading"
                                                    helperText={(() => {
                                                        const m = (detail.meters ?? []).find(x => x.id === meterId);
                                                        return m?.lastValue != null ? `Last reading: ${m.lastValue} ${m.unit}` : undefined;
                                                    })()}
                                                    onChange={e => setMeterValue(e.target.value)}
                                                />
                                                <TextField
                                                    size="small" type="datetime-local" label="Read at (default now)" value={meterReadAt} data-help="maint.woDialog.field.readAt"
                                                    InputLabelProps={{ shrink: true }} onChange={e => setMeterReadAt(e.target.value)}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {dialog === 'close' && (
                            <>
                                {codeSelect('Cause', 'CAUSE', causeId, setCauseId, { 'data-help': 'maint.woDialog.field.cause' })}
                                {codeSelect('Action taken', 'ACTION', actionId, setActionId, { 'data-help': 'maint.woDialog.field.action' })}
                            </>
                        )}
                        {dialog === 'cancel' && (
                            <TextField size="small" label="Reason" value={reason} multiline minRows={2} required data-help="maint.woDialog.field.cancelReason" onChange={e => setReason(e.target.value)} />
                        )}
                        {dialog === 'reopen' && (
                            <FormControlLabel
                                control={<Checkbox checked={machineDownAgain} onChange={e => setMachineDownAgain(e.target.checked)} />}
                                label="The machine is down again" data-help="maint.woDialog.field.downAgain"
                            />
                        )}
                        {dialog === 'checklist' && (
                            <TextField size="small" label="Task" value={task} required data-help="maint.woDialog.field.task" onChange={e => setTask(e.target.value)} />
                        )}
                        {dialog === 'parts' && (
                            <>
                                <SearchableSelect
                                    options={items} value={itemId} size="small" label="Spare part" data-help="maint.woDialog.field.part"
                                    getOptionLabel={i => `${i.itemCode} - ${i.itemName}`}
                                    disableClientFilter onInputChange={searchItems} onChange={v => setItemId(v)}
                                />
                                <TextField size="small" type="number" label="Quantity" value={qty} data-help="maint.woDialog.field.qty" onChange={e => setQty(e.target.value)} />
                            </>
                        )}
                        {dialog === 'attach' && (
                            <TextField size="small" label="Requisition reference" value={refNo} required data-help="maint.woDialog.field.refNo" onChange={e => setRefNo(e.target.value)} />
                        )}
                        {dialog === 'external' && (
                            <>
                                <TextField size="small" label="Vendor" value={vendor} data-help="maint.woDialog.field.vendor" onChange={e => setVendor(e.target.value)} />
                                <TextField size="small" type="number" label="Cost (memo only)" value={costMemo} data-help="maint.woDialog.field.cost" onChange={e => setCostMemo(e.target.value)} />
                                <TextField size="small" label="Bill reference" value={billRef} data-help="maint.woDialog.field.billRef" onChange={e => setBillRef(e.target.value)} />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)} data-help="maint.woDialog.cancel">Cancel</Button>
                    <Button data-help="maint.workOrder.action.confirm" variant="contained" disabled={saving || !canSubmit} onClick={submit}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

function dialogTitle(k: DialogKind): string {
    switch (k) {
        case 'assign': return 'Assign technician';
        case 'hold': return 'Put on hold';
        case 'complete': return 'Complete work order';
        case 'close': return 'Close work order';
        case 'cancel': return 'Cancel work order';
        case 'reopen': return 'Reopen work order';
        case 'labour': return 'Add labour';
        case 'checklist': return 'Add checklist item';
        case 'parts': return 'Request spare parts';
        case 'attach': return 'Attach an existing requisition';
        case 'external': return 'External repair';
        default: return '';
    }
}

export default WorkOrderDetail;
